/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { Row } from '@tanstack/react-table'
import clsx from 'clsx'
import UiTableRowCellsList from './UiTableRowCellsList'

interface UiTableRowItemProps {
    row: Row<any>
    decorationId: string
    onRowClick?: (rowData: any) => void
    onRowInteraction?: (rowData: any) => void
    hasRowsSelected?: boolean
}

const ROW_INTERACTION_BLOCK_SELECTOR = '[data-row-selection-toggle="true"]'

const isRowInteractionBlocked = (target: EventTarget | null): boolean =>
    (target as HTMLElement | null)?.closest(ROW_INTERACTION_BLOCK_SELECTOR) != null

const UiTableRowItem: React.FC<UiTableRowItemProps> = ({
    row,
    decorationId,
    onRowClick,
    onRowInteraction,
    hasRowsSelected = false,
}) => {
    const handleRowInteractionCapture = (event: React.MouseEvent<HTMLTableRowElement>) => {
        if (isRowInteractionBlocked(event.target)) {
            return
        }

        if (hasRowsSelected) {
            return
        }

        if (onRowInteraction) {
            onRowInteraction(row)
        }
    }

    const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
        if (isRowInteractionBlocked(event.target)) {
            return
        }

        if (!hasRowsSelected && onRowClick) {
            onRowClick(row)
        }
    }

    return (
        <tr
            data-row-scroll-id={row.id}
            data-row-original-id={
                row.original && typeof row.original === 'object' && 'id' in row.original
                    ? String((row.original as { id?: string | number }).id ?? '')
                    : ''
            }
            className={clsx(
                'UiTableRowItem border-b border-b-gray-200/60 last:border-b-0 transition-colors',
                hasRowsSelected
                    ? 'cursor-default'
                    : 'cursor-pointer hover:bg-blue-50/80! dark:hover:bg-blue-900/40!',
                'dark:border-b-gray-800/80'
            )}
            onClickCapture={handleRowInteractionCapture}
            onClick={handleRowClick}
        >
            <UiTableRowCellsList
                cells={row.getVisibleCells()}
                decorationId={decorationId}
            />
        </tr>
    )
}

export default UiTableRowItem
